import type { IssuedCredentials } from '../credentials/issued-credentials'
import { useMutation, useQuery } from '@apollo/client/react'
import { RoleEnum } from '@zeus/index'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import FieldError from '~/components/FieldError'
import RequiredFieldsHint from '~/components/RequiredFieldsHint'
import { SegmentedControl } from '~/components/SegmentedControl'
import { SidePanel } from '~/components/SidePanel'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { GQL_ROLES } from '~/gql/role/role'
import { GQL_ADD_USER, GQL_UPDATE_USER } from '~/gql/users/users'
import { client } from '~/helpers/apollo'
import { errorAria } from '~/helpers/forms/error-aria'
import { useAuth } from '~/helpers/store/auth'
import { roleLabel } from '~/helpers/users/role-label'
import AffiliationFields from './AffiliationFields'
import GroupNameSelect from './GroupNameSelect'
import UsernameLearnerForm, { USERNAME_LEARNER_FORM_ID } from './UsernameLearnerForm'

type Props = {
  open: boolean
  defaultValues?: {
    id?: string
    email?: string
    firstName?: string
    lastName?: string
    roleId?: string
    /** Pseudo d'un apprenant sans e-mail (#271) : son e-mail n'est alors pas exigé. */
    username?: string | null
    company?: string | null
    department?: string | null
  }
  onCancel: () => void
  onSuccess: () => void
  /** Un apprenant en pseudo vient d'être créé : ses identifiants, à afficher une fois. */
  onIssued?: (credentials: IssuedCredentials) => void
}

type CreationMode = 'email' | 'username'

type FormData = {
  id?: string
  email?: string
  firstName?: string
  lastName?: string
  roleId?: string
  /**
   * Nom du groupe, et non son id : c'est le champ que porte `CreateUserInput`,
   * partagé tel quel avec l'import CSV (#135). Vide = aucun rattachement.
   */
  group?: string
  company?: string
  department?: string
}

/** Clé i18n du refus d'une création : adresse prise, démo pleine (#350), ou échec générique. */
function addUserErrorKey(message: string): string {
  if (message.includes('EMAIL_ALREADY_USED')) return 'add.user.emailUsed'
  if (message.includes('DEMO_LEARNER_LIMIT')) return 'demo.learnerLimit'
  return 'bad.add.user'
}

function UserModal({ open, onCancel, onSuccess, onIssued, defaultValues }: Props) {
  const { data } = useQuery(GQL_ROLES)

  const { t } = useTranslation()
  const { payload } = useAuth()

  const { register, handleSubmit, setValue, getValues, watch, reset, formState } = useForm<FormData>({
    defaultValues: {
      id: defaultValues?.id || '',
      email: defaultValues?.email || '',
      firstName: defaultValues?.firstName || '',
      lastName: defaultValues?.lastName || '',
      roleId: defaultValues?.roleId || '',
      group: '',
      company: defaultValues?.company || '',
      department: defaultValues?.department || '',
    },
  })

  const isUpdate = !!getValues('id')
  const [mode, setMode] = useState<CreationMode>('email')
  const usernameMode = !isUpdate && mode === 'username'
  // Apprenant sans e-mail en édition : ni e-mail exigé, ni nom, ni changement de rôle (verrouillé par l'API).
  const usernameAccount = isUpdate && !!defaultValues?.username && !defaultValues.email

  const [updateUser] = useMutation(GQL_UPDATE_USER, {
    client,
  })

  // `createUser` provisionne l'invitation avec les vraies valeurs saisies et le
  // rôle choisi, là où l'ancien `askForRegister` écrivait « Unknown » en dur et
  // ignorait le rôle (#148). C'est le même chemin que l'import CSV.
  const [addUser] = useMutation(GQL_ADD_USER, {
    client,
  })

  const onSubmit = async () => {
    // Si c'est une mise à jour d'utilisateur
    if (isUpdate) {
      try {
        await updateUser({
          variables: {
            updateUserInput: {
              id: getValues('id') || '',
              email: getValues('email') || '',
              firstName: getValues('firstName') || '',
              lastName: getValues('lastName') || '',
              roleId: getValues('roleId') || '',
              // Vide = effacé côté API (#277), contrairement aux noms.
              company: getValues('company') ?? '',
              department: getValues('department') ?? '',
            },
          },
        })
        toast.success(t('good.update.user'))
        onSuccess()
        onCancel()
        reset()
      } catch (error) {
        // Un encadrant qui devient apprenant prend une place de la démo (#350).
        const message = error instanceof Error ? error.message : ''
        toast.error(t(message.includes('DEMO_LEARNER_LIMIT') ? 'demo.learnerLimit' : 'bad.update.user'))
      }
      return
    }

    try {
      await addUser({
        variables: {
          createUserInput: {
            email: getValues('email') || '',
            firstName: getValues('firstName') || '',
            lastName: getValues('lastName') || '',
            roleId: getValues('roleId') || '',
            // Champ optionnel : omis plutôt que vide, pour ne pas demander au
            // serveur de résoudre un groupe au libellé blanc.
            group: getValues('group') || undefined,
            company: getValues('company') || undefined,
            department: getValues('department') || undefined,
          },
        },
      })
      toast.success(t('good.add.user'))
      onSuccess()
      onCancel()
      reset()
    } catch (error) {
      const message = error instanceof Error ? error.message : ''
      toast.error(t(addUserErrorKey(message)))
    }
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={onCancel}
      title={isUpdate ? t('update.user') : t('create.user')}
      // Le pied du tiroir est hors du <form> : le bouton s'y rattache par son id.
      footer={(
        <>
          <Button variant="outline" onClick={onCancel} type="button">
            {t('common.cancel')}
          </Button>
          <Button type="submit" form={usernameMode ? USERNAME_LEARNER_FORM_ID : 'user-form'}>{isUpdate ? t('common.update') : t('common.create')}</Button>
        </>
      )}
    >
      {!isUpdate && (
        <div className="mb-4 space-y-2">
          <SegmentedControl
            label={t('user.mode.label')}
            options={[{ value: 'email', label: t('user.mode.email') }, { value: 'username', label: t('user.mode.username') }]}
            value={mode}
            onChange={setMode}
          />
          {usernameMode && <p className="text-xs text-muted-foreground">{t('user.mode.username.help')}</p>}
        </div>
      )}
      {usernameMode && (
        <UsernameLearnerForm
          onCreated={(credentials) => {
            onSuccess()
            onIssued?.(credentials)
            onCancel()
          }}
        />
      )}
      <form id="user-form" hidden={usernameMode} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <RequiredFieldsHint />
        {usernameAccount && (
          <p className="text-sm">
            <span className="text-muted-foreground">
              {t('common.username')}
              {' '}
              :
              {' '}
            </span>
            <span className="font-mono font-semibold">{defaultValues?.username}</span>
          </p>
        )}
        <div className="space-y-2" hidden={usernameAccount}>
          <Label htmlFor="email" required>{t('common.email')}</Label>
          <Input
            id="email"
            aria-required={!usernameAccount}
            {...errorAria('email', formState.errors.email)}
            type="email"
            {...register('email', {
              onChange: (e) => {
                setValue('email', e.target.value)
              },
              required: usernameAccount ? false : t('input.required.message'),
              pattern: {
                value: /^[\w\-\\.]+@([\w\-]+\.)+[\w\-]{2,4}$/g,
                message: t('input.email.message'),
              },
            })}
          />
          <FieldError fieldId="email" message={formState.errors.email?.message} />
        </div>

        {/*
          Prénom et nom sont requis dans les deux modes : à la création, les
          omettre laissait le compte à « Unknown » jusqu'à une édition
          manuelle, alors que l'import CSV les exige déjà (#148).
        */}
        <div className="space-y-2">
          <Label htmlFor="lastName" required={!usernameAccount}>{t('common.lastname')}</Label>
          <Input
            id="lastName"
            aria-required={!usernameAccount}
            {...errorAria('lastName', formState.errors.lastName)}
            type="text"
            {...register('lastName', {
              onChange: (e) => {
                setValue('lastName', e.target.value)
              },
              required: usernameAccount ? false : t('input.required.message'),
            })}
          />
          <FieldError fieldId="lastName" message={formState.errors.lastName?.message} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="firstName" required={!usernameAccount}>{t('common.firstname')}</Label>
          <Input
            id="firstName"
            aria-required={!usernameAccount}
            {...errorAria('firstName', formState.errors.firstName)}
            type="text"
            {...register('firstName', {
              onChange: (e) => {
                setValue('firstName', e.target.value)
              },
              required: usernameAccount ? false : t('input.required.message'),
            })}
          />
          <FieldError fieldId="firstName" message={formState.errors.firstName?.message} />
        </div>

        <AffiliationFields idPrefix="user" company={register('company')} department={register('department')} />

        {/*
          Rattachement au groupe, proposé à la création seulement — et sur les
          deux parcours à la fois (import CSV compris), les traiter séparément
          étant exactement ce qui a produit #148.
        */}
        {!isUpdate && (
          <GroupNameSelect id="group" value={watch('group') ?? ''} onChange={value => setValue('group', value)} />
        )}

        {
          payload && payload.role === RoleEnum.Admin && !usernameAccount && (
            <div className="space-y-2">
              <Label htmlFor="role">{t('common.role')}</Label>
              <Select
                value={getValues('roleId')}
                onValueChange={(value: string) => {
                  setValue('roleId', value, { shouldValidate: true })
                }}
              >
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {data?.getRoles.map(role => (
                    <SelectItem key={role.id} value={role.id}>
                      {roleLabel(t, role.name)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )
        }
      </form>
    </SidePanel>
  )
}

export default UserModal
